<script lang="ts">
export default {
  name: 'ConsolidatedEditPolicyModal',
};
</script>

<script setup lang="ts">
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { FileText, LayoutList, ArrowRight, ArrowLeft, Check, History } from '@lucide/vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import PolicyEditFormPanel from '@/components/v2/policy/PolicyEditFormPanel.vue';
import PolicyEditReviewPanel from '@/components/v2/policy/PolicyEditReviewPanel.vue';
import PolicyEditHistoryPanel from '@/components/v2/policy/PolicyEditHistoryPanel.vue';
import PolicyServicingRolesPanel from '@/components/v2/policy/PolicyServicingRolesPanel.vue';
import PolicySubitemsPanel from '@/components/v2/policy/PolicySubitemsPanel.vue';
import {
  usePolicyEditPending,
  type PolicyRuleViolationFlags,
  type RecordKey,
  type RecordRef,
  type SubitemKind,
  type ValueLabelLookup,
} from '@/composables/usePolicyEditPending';
import type { SubitemTypeRef } from '@/types/PolicySublimit';
import { useToast } from '@/composables/useToast';
import { policyOverrideService, referenceService, carrierService, policySourceService, policyService as v1PolicyService } from '@/services/api';
import type { PolicySource } from '@/types/PolicySource';
import type { EpicPolicy } from '@/types/tower/Domain';
import type { AuditLog } from '@/types/AuditLog';
import type { PolicyWithChildren } from '@/utilities/Policies';

interface SelectOption {
  label: string;
  value: string;
}

type EditTab = 'structure' | 'servicing' | 'sublimits' | 'subretentions';

const props = defineProps<{
  show: boolean;
  policy: PolicyWithChildren;
  policySourceId?: number;
  initialRecordKey?: RecordKey;
  initialTab?: EditTab;
  // Client-readiness rule flags for the policy record; the form panel marks
  // the flagged fields until the violations are resolved.
  ruleViolations?: PolicyRuleViolationFlags;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save'): void;
}>();

const { success, error } = useToast();
const pending = usePolicyEditPending();

const view = ref<'edit' | 'review'>('edit');
const activeRecord = ref<RecordKey>('policy');
const activeTab = ref<EditTab>('structure');
const subitemsLoading = ref(false);
const loading = ref(false);
const saving = ref(false);
const recordLoading = ref(false);

const syndicateOptions = ref<SelectOption[]>([]);
const carrierOptions = ref<SelectOption[]>([]);
const sideAOptions = ref<string[]>(['None', 'Side A', 'Side A DIC', 'Side A IDL']);
const policySource = ref<PolicySource | undefined>(undefined);

const showHistory = ref(false);
const historyLoading = ref(false);
const historyCache = reactive(new Map<RecordKey, AuditLog[]>());

const recordRefs = computed<RecordRef[]>(() => {
  const refs: RecordRef[] = [
    {
      key: 'policy',
      type: 'policy',
      uniqPolicy: String(props.policy.uniqPolicy ?? ''),
      label: props.policy.coverageType || props.policy.description || 'Policy',
      subLabel: props.policy.policyID,
    },
  ];

  const children = Array.isArray(props.policy.children) ? props.policy.children : [];
  for (const line of children) {
    if (!line.uniqLine) continue;
    refs.push({
      key: `line:${line.uniqLine}`,
      type: 'line',
      uniqPolicy: String(line.uniqPolicy ?? props.policy.uniqPolicy ?? ''),
      uniqLine: String(line.uniqLine),
      label: line.coverageType || line.description || 'Line',
      subLabel: line.policyID,
    });
  }
  return refs;
});

const activeRef = computed(() => recordRefs.value.find((r) => r.key === activeRecord.value));
const activeState = computed(() => pending.get(activeRecord.value));

const dirtyCount = computed(() => pending.dirtyKeys.value.length);

const diffValueLabels = computed<ValueLabelLookup>(() => {
  const syndicate = new Map<string, string>();
  for (const opt of syndicateOptions.value) syndicate.set(opt.value, opt.label);
  const carrier = new Map<string, string>();
  for (const opt of carrierOptions.value) carrier.set(opt.value, opt.label);
  return { syndicateId: syndicate, carrierOverride: carrier };
});

const reviewRecords = computed(() =>
  pending.dirtyKeys.value
    .map((key) => {
      const ref = recordRefs.value.find((r) => r.key === key);
      if (!ref) return null;
      return { ref, diffs: pending.buildDiffs(key, diffValueLabels.value) };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null && r.diffs.length > 0),
);

const footerHint = computed(() => {
  if (view.value === 'review') return 'Review all changes before saving';
  if (dirtyCount.value === 0) {
    const ref = activeRef.value;
    if (!ref) return '';
    return ref.type === 'policy' ? 'Editing policy record' : `Editing ${ref.label} line`;
  }
  return `${dirtyCount.value} record${dirtyCount.value > 1 ? 's' : ''} with unsaved changes`;
});

const recordIcon = (ref: RecordRef) => (ref.type === 'policy' ? FileText : LayoutList);

async function ensureChildrenLoaded(): Promise<void> {
  if (!props.policy.hasChildren) return;
  if (Array.isArray(props.policy.children) && props.policy.children.length > 0) return;
  try {
    const children = await v1PolicyService.children(props.policy.uniqPolicy!);
    // mutate prop in place so reactivity flows through (parent caches this on its own list too)
    (props.policy as PolicyWithChildren).children = children as EpicPolicy[];
  } catch (e) {
    console.error('Failed to load policy children', e);
  }
}

async function loadReferenceData(): Promise<void> {
  if (syndicateOptions.value.length && carrierOptions.value.length) return;
  const [syndicates, carriers] = await Promise.all([referenceService.syndicates(), carrierService.list()]);
  syndicateOptions.value = syndicates.map((s) => ({ label: `${s.number}-${s.name}`, value: s.id.toString() }));
  carrierOptions.value = carriers.map((c) => ({ label: c.name, value: c.ID.toString() }));
}

async function loadPolicySource(): Promise<void> {
  if (!props.policySourceId) {
    policySource.value = undefined;
    return;
  }
  try {
    policySource.value = await policySourceService.getPolicySourceById(props.policySourceId);
  } catch (e) {
    console.error('Failed to load policy source', e);
  }
}

async function selectRecord(key: RecordKey, tab: EditTab = 'structure'): Promise<void> {
  activeRecord.value = key;
  showHistory.value = false;
  recordLoading.value = true;
  try {
    await pending.loadRecord(key);
    await selectTab(isSubitemTabAvailable(key, tab) ? tab : 'structure');
  } catch (e) {
    console.error('Failed to load record', key, e);
    error('Failed to load record data');
  } finally {
    recordLoading.value = false;
  }
}

// Sublimits and subretentions hang off a policy line, so their tabs only exist on records with one.
function isSubitemTabAvailable(key: RecordKey, tab: EditTab): boolean {
  if (tab !== 'sublimits' && tab !== 'subretentions') return true;
  return !!pending.subitemLineId(key);
}

async function selectTab(tab: EditTab): Promise<void> {
  activeTab.value = tab;
  if (tab !== 'sublimits' && tab !== 'subretentions') return;

  subitemsLoading.value = true;
  try {
    await pending.loadSubitemTypes(activeRecord.value);
  } catch (e) {
    console.error('Failed to load subitem types', e);
    error('Failed to load sublimit and subretention types');
  } finally {
    subitemsLoading.value = false;
  }
}

function subitemRows(kind: SubitemKind) {
  return pending.subitemRows(activeRecord.value, kind);
}

function availableSubitemTypes(kind: SubitemKind) {
  return pending.availableSubitemTypes(activeRecord.value, kind);
}

function onSubitemAdd(kind: SubitemKind, type: SubitemTypeRef): void {
  pending.stageSubitemAdd(activeRecord.value, kind, type);
}

function onSubitemUpdate(kind: SubitemKind, payload: { rowKey: string; value: string; basis?: string }): void {
  pending.setSubitemValue(activeRecord.value, kind, payload.rowKey, payload.value, payload.basis);
}

function onSubitemRemove(kind: SubitemKind, rowKey: string): void {
  pending.stageSubitemDelete(activeRecord.value, kind, rowKey);
}

function onSubitemUndo(kind: SubitemKind, rowKey: string): void {
  pending.unstageSubitem(activeRecord.value, kind, rowKey);
}

async function loadHistoryForRecord(key: RecordKey): Promise<void> {
  if (historyCache.has(key)) return;
  const ref = recordRefs.value.find((r) => r.key === key);
  if (!ref) return;

  historyLoading.value = true;
  try {
    const logs =
      ref.type === 'policy'
        ? await policyOverrideService.GetOverrideHistory(ref.uniqPolicy)
        : await policyOverrideService.GetLineOverrideHistory(ref.uniqPolicy, ref.uniqLine!);
    historyCache.set(key, logs);
  } catch (e) {
    console.error('Failed to load history for', key, e);
    historyCache.set(key, []);
  } finally {
    historyLoading.value = false;
  }
}

async function toggleHistory(): Promise<void> {
  showHistory.value = !showHistory.value;
  if (showHistory.value) {
    await loadHistoryForRecord(activeRecord.value);
  }
}

async function initialize(): Promise<void> {
  loading.value = true;
  view.value = 'edit';
  activeTab.value = 'structure';
  showHistory.value = false;
  historyCache.clear();
  try {
    await Promise.all([ensureChildrenLoaded(), loadReferenceData(), loadPolicySource()]);
    pending.init(recordRefs.value);
    pending.configureCaps({
      carrierOptions: carrierOptions.value,
      allowCarrierUpdate: !!policySource.value?.AllowCarrierUpdate,
      allowPremiumUpdate: !!policySource.value?.AllowPremiumUpdate,
    });
    try {
      await pending.loadSubitems(String(props.policy.uniqPolicy ?? ''));
    } catch (e) {
      // Surfaced, not just logged: a silent failure here leaves the tabs looking empty rather
      // than broken, which reads as "this line has none".
      console.error('Failed to load sublimits and subretentions', e);
      error('Failed to load sublimits and subretentions');
    }
    const startKey = props.initialRecordKey && recordRefs.value.some((r) => r.key === props.initialRecordKey) ? props.initialRecordKey : 'policy';
    await selectRecord(startKey, props.initialTab ?? 'structure');
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.show,
  (show) => {
    if (show) {
      initialize();
    }
  },
);

onMounted(() => {
  if (props.show) initialize();
});

function goToReview(): void {
  if (!pending.validateAll()) {
    error('Please fix invalid fields before reviewing');
    return;
  }
  view.value = 'review';
}

function backToEdit(): void {
  view.value = 'edit';
}

function handleClose(): void {
  emit('close');
}

async function confirmSave(): Promise<void> {
  const payload = pending.buildBatchPayload();
  if (!payload.policyOverride && !payload.lineOverrides && !payload.sublimitOps && !payload.subretentionOps) {
    handleClose();
    return;
  }

  saving.value = true;
  try {
    await policyOverrideService.saveOverrideBatch(String(props.policy.uniqPolicy ?? ''), payload);
    pending.resetOriginalsFromCurrent();
    // Staged rows were just cleared, so the panels would fall back to the pre-save lists until
    // the modal is reopened. Refetch instead of leaving a saved item invisible. A failure here
    // must not report the save as failed — it already succeeded.
    try {
      await pending.loadSubitems(String(props.policy.uniqPolicy ?? ''));
    } catch (e) {
      console.error('Saved, but failed to refresh sublimits and subretentions', e);
    }
    success('Changes saved successfully');
    emit('save');
  } catch (e) {
    console.error('Failed to save policy batch', e);
    error('Error saving policy changes');
  } finally {
    saving.value = false;
  }
}

const policyNumber = computed(() => props.policy.policyID || String(props.policy.uniqPolicy ?? ''));

const activeHistory = computed<AuditLog[]>(() => historyCache.get(activeRecord.value) ?? []);
</script>

<template>
  <AppModal
    :show="show"
    size="xl"
    modal-class="max-h-[90vh] max-w-5xl"
    :handle-cancel="handleClose"
    :handle-action="goToReview">
    <template #title>
      <div class="flex items-center justify-between">
        <h2 class="text-base font-semibold text-primary">
          Edit policy
          <span class="ml-2 font-mono text-xs font-normal text-slate-light">{{ policyNumber }}</span>
        </h2>
        <AppButton
          v-if="view === 'edit'"
          size="xs"
          :disabled="loading"
          @click="toggleHistory">
          <History class="h-3.5 w-3.5" />
          {{ showHistory ? 'Exit history' : 'Show history' }}
        </AppButton>
      </div>
    </template>

    <template #content>
      <div
        v-if="loading"
        class="flex h-64 items-center justify-center">
        <span class="loading loading-md loading-spinner"></span>
      </div>

      <div
        v-else
        class="flex h-full min-h-[480px] gap-4">
        <!-- Left nav -->
        <aside
          v-if="view === 'edit'"
          class="w-52 shrink-0 overflow-y-auto border-r border-base-300 pr-3">
          <div class="px-2 py-1 text-xs font-semibold tracking-wide text-slate-light uppercase">Policy</div>
          <template
            v-for="ref in recordRefs"
            :key="ref.key">
            <div
              v-if="ref.type === 'policy'"
              class="cursor-pointer rounded px-3 py-2 transition-colors"
              :class="activeRecord === ref.key ? 'border border-primary bg-primary/10 text-primary' : 'border border-transparent hover:bg-base-200'"
              @click="selectRecord(ref.key)">
              <div class="flex items-center gap-2">
                <component
                  :is="recordIcon(ref)"
                  class="h-4 w-4 shrink-0"
                  :class="activeRecord === ref.key ? 'text-primary' : 'text-slate-light'" />
                <div class="min-w-0 flex-1">
                  <div
                    class="truncate text-sm font-medium"
                    :class="activeRecord === ref.key ? 'text-primary' : 'text-slate-dark'">
                    {{ ref.label }}
                  </div>
                  <div
                    class="truncate text-xs"
                    :class="activeRecord === ref.key ? 'text-secondary' : 'text-slate-medium'">
                    Policy record
                  </div>
                </div>
                <span
                  v-if="pending.isDirty(ref.key)"
                  class="h-2 w-2 shrink-0 rounded-full bg-warning"></span>
              </div>
            </div>
          </template>

          <template v-if="recordRefs.some((r) => r.type === 'line')">
            <div class="mt-3 px-2 py-1 text-xs font-semibold tracking-wide text-slate-light uppercase">Policy lines</div>
            <template
              v-for="ref in recordRefs"
              :key="ref.key">
              <div
                v-if="ref.type === 'line'"
                class="mt-0.5 cursor-pointer rounded px-3 py-2 pl-4 transition-colors"
                :class="activeRecord === ref.key ? 'border border-primary bg-primary/10 text-primary' : 'border border-transparent hover:bg-base-200'"
                @click="selectRecord(ref.key)">
                <div class="flex items-center gap-2">
                  <component
                    :is="recordIcon(ref)"
                    class="h-4 w-4 shrink-0"
                    :class="activeRecord === ref.key ? 'text-primary' : 'text-slate-light'" />
                  <div class="min-w-0 flex-1">
                    <div
                      class="truncate text-sm font-medium"
                      :class="activeRecord === ref.key ? 'text-primary' : 'text-slate-dark'">
                      {{ ref.label }}
                    </div>
                    <div
                      v-if="ref.subLabel"
                      class="truncate text-xs"
                      :class="activeRecord === ref.key ? 'text-secondary' : 'text-slate-medium'">
                      {{ ref.subLabel }}
                    </div>
                  </div>
                  <span
                    v-if="pending.isDirty(ref.key)"
                    class="h-2 w-2 shrink-0 rounded-full bg-warning"></span>
                </div>
              </div>
            </template>
          </template>
        </aside>

        <!-- Right pane -->
        <section class="min-w-0 flex-1 overflow-y-auto px-3 py-1">
          <template v-if="view === 'edit'">
            <template v-if="showHistory">
              <PolicyEditHistoryPanel
                v-if="activeRef"
                :audit-logs="activeHistory"
                :loading="historyLoading"
                :record-label="activeRef.label" />
            </template>

            <template v-else>
              <div class="mb-4 flex w-fit gap-1 rounded bg-base-200 p-1">
                <button
                  class="rounded px-3 py-1 text-xs font-medium transition-colors"
                  :class="activeTab === 'structure' ? 'bg-base-100 text-primary shadow-sm' : 'text-slate-medium hover:text-slate-dark'"
                  @click="selectTab('structure')">
                  Structure
                </button>
                <button
                  class="rounded px-3 py-1 text-xs font-medium transition-colors"
                  :class="activeTab === 'servicing' ? 'bg-base-100 text-primary shadow-sm' : 'text-slate-medium hover:text-slate-dark'"
                  @click="selectTab('servicing')">
                  Servicing roles
                </button>
                <template v-if="pending.subitemLineId(activeRecord)">
                  <button
                    class="rounded px-3 py-1 text-xs font-medium transition-colors"
                    :class="activeTab === 'sublimits' ? 'bg-base-100 text-primary shadow-sm' : 'text-slate-medium hover:text-slate-dark'"
                    data-testid="tab-sublimits"
                    @click="selectTab('sublimits')">
                    Sublimits
                    <span
                      v-if="pending.stagedSubitemCount(activeRecord, 'sublimit') > 0"
                      class="ml-1 badge border-0 bg-warning/20 badge-xs text-warning"
                      >{{ pending.stagedSubitemCount(activeRecord, 'sublimit') }}</span
                    >
                  </button>
                  <button
                    class="rounded px-3 py-1 text-xs font-medium transition-colors"
                    :class="activeTab === 'subretentions' ? 'bg-base-100 text-primary shadow-sm' : 'text-slate-medium hover:text-slate-dark'"
                    data-testid="tab-subretentions"
                    @click="selectTab('subretentions')">
                    Subretentions
                    <span
                      v-if="pending.stagedSubitemCount(activeRecord, 'subretention') > 0"
                      class="ml-1 badge border-0 bg-warning/20 badge-xs text-warning"
                      >{{ pending.stagedSubitemCount(activeRecord, 'subretention') }}</span
                    >
                  </button>
                </template>
              </div>

              <div
                v-if="recordLoading"
                class="flex h-48 items-center justify-center">
                <span class="loading loading-md loading-spinner"></span>
              </div>

              <template v-else-if="activeRef && activeState">
                <PolicyEditFormPanel
                  v-if="activeTab === 'structure'"
                  :mode="activeRef.type"
                  v-model:form="activeState.form"
                  :original-form="activeState.originalForm"
                  :valid-fields="activeState.validFields"
                  :is-disabled="saving"
                  :policy-source="policySource"
                  :syndicate-options="syndicateOptions"
                  :carrier-options="carrierOptions"
                  :side-a-options="sideAOptions"
                  :rule-violations="activeRef.type === 'policy' ? ruleViolations : undefined" />

                <PolicyServicingRolesPanel
                  v-else-if="activeTab === 'servicing'"
                  :key="activeRef.key"
                  :policy-id="Number(activeRef.uniqPolicy)"
                  :policy-line-id="activeRef.uniqLine ? Number(activeRef.uniqLine) : undefined" />

                <PolicySubitemsPanel
                  v-else-if="activeTab === 'sublimits'"
                  :key="`${activeRef.key}-sublimits`"
                  kind="sublimit"
                  :rows="subitemRows('sublimit')"
                  :available-types="availableSubitemTypes('sublimit')"
                  :is-disabled="saving"
                  :loading="subitemsLoading"
                  @add="onSubitemAdd('sublimit', $event)"
                  @update="onSubitemUpdate('sublimit', $event)"
                  @remove="onSubitemRemove('sublimit', $event)"
                  @undo="onSubitemUndo('sublimit', $event)" />

                <PolicySubitemsPanel
                  v-else-if="activeTab === 'subretentions'"
                  :key="`${activeRef.key}-subretentions`"
                  kind="subretention"
                  :rows="subitemRows('subretention')"
                  :available-types="availableSubitemTypes('subretention')"
                  :is-disabled="saving"
                  :loading="subitemsLoading"
                  @add="onSubitemAdd('subretention', $event)"
                  @update="onSubitemUpdate('subretention', $event)"
                  @remove="onSubitemRemove('subretention', $event)"
                  @undo="onSubitemUndo('subretention', $event)" />
              </template>
            </template>
          </template>

          <template v-else>
            <PolicyEditReviewPanel :records="reviewRecords" />
          </template>
        </section>
      </div>
    </template>

    <template #actions>
      <div class="flex flex-1 items-center justify-between">
        <span
          class="text-xs"
          :class="dirtyCount > 0 && view === 'edit' ? 'text-warning' : 'text-slate-medium'">
          {{ footerHint }}
        </span>
        <div class="flex gap-2">
          <button
            v-if="view === 'edit'"
            class="btn btn-sm"
            :disabled="saving"
            @click="handleClose">
            Cancel
          </button>
          <button
            v-if="view === 'review'"
            class="btn btn-sm"
            :disabled="saving"
            @click="backToEdit">
            <ArrowLeft class="h-3.5 w-3.5" />
            Back to edit
          </button>
          <button
            v-if="view === 'edit'"
            class="btn text-white btn-sm btn-primary"
            :disabled="dirtyCount === 0 || saving"
            @click="goToReview">
            Review &amp; save
            <ArrowRight class="h-3.5 w-3.5" />
          </button>
          <button
            v-else
            class="btn text-white btn-sm btn-primary"
            :disabled="saving"
            @click="confirmSave">
            <span
              v-if="saving"
              class="loading loading-xs loading-dots"></span>
            <template v-else>
              <Check class="h-4 w-4" />
              Confirm &amp; save
            </template>
          </button>
        </div>
      </div>
    </template>
  </AppModal>
</template>
